/**
 * UpgradePrompt Component
 *
 * Displays a modal or inline prompt when users try to access
 * features that require a higher tier subscription.
 *
 * Automatically tracks:
 * - Impressions: When the prompt is shown
 * - Clicks: When the upgrade button is clicked
 */

import { useEffect, useRef, useCallback } from "react";
import { X, Lock, Sparkles, ArrowRight, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { Modal } from "@/components/ui/Modal";

type PromptLocation =
  | "sources_page"
  | "source_detail"
  | "dashboard"
  | "remediation_page"
  | "remediation_detail"
  | "domain_settings"
  | "apex_page"
  | "sidebar"
  | "modal";

type Severity = "low" | "medium" | "high";

interface DynamicVars {
  sourceCount?: number;
  taskCount?: number;
  providerName?: string;
  passRate?: number;
  domainCount?: number;
  failingCount?: number;
}

interface UpgradePromptProps {
  feature: string;
  currentTier: string;
  requiredTier: string;
  issueCount?: number;
  onUpgrade: () => void;
  onDismiss: () => void;
  variant?: "modal" | "inline" | "banner";
  /** Location where the prompt is shown (for analytics) */
  location?: PromptLocation;
  /** Severity level for escalated messaging */
  severity?: Severity;
  /** Dynamic data for personalized copy */
  dynamicVars?: DynamicVars;
}

interface SeverityMessage {
  description: string | ((vars: DynamicVars) => string);
  urgencyNote?: string;
}

interface FeatureMessage {
  title: string;
  description: string | ((vars: DynamicVars) => string);
  benefits: string[];
  severityMessages?: Record<Severity, SeverityMessage>;
}

const FEATURE_MESSAGES: Record<string, FeatureMessage> = {
  // === Free → Starter gates ===
  source_labeling: {
    title: "Know Every Sender by Name",
    description:
      "Categorize your sending sources and get targeted remediation guidance.",
    benefits: [
      "Identify legitimate senders vs. threats",
      "Auto-generate prioritized fix tasks",
      "Track authorization across all providers",
    ],
    severityMessages: {
      low: {
        description:
          "Organize your email senders and maintain clean authentication records.",
      },
      medium: {
        description: (vars) =>
          `You have ${vars.sourceCount ?? "several"} unidentified sources sending as your domain. Label them to understand who is legitimate.`,
      },
      high: {
        description: (vars) =>
          `${vars.sourceCount ?? "Multiple"} unknown sources are actively sending as your domain. Without labeling, you cannot tell threats from legitimate senders.`,
        urgencyNote: "Unidentified senders increase your spoofing risk.",
      },
    },
  },
  hostname_view: {
    title: "See Who's Really Sending",
    description:
      "View full sender details including hostnames, organizations, and verified provider information.",
    benefits: [
      "PTR hostname resolution",
      "Organization identification",
      "Automatic provider matching",
    ],
    severityMessages: {
      low: {
        description:
          "Get full visibility into every IP sending email as your domain.",
      },
      medium: {
        description: (vars) =>
          `${vars.sourceCount ?? "Several"} sources are sending as your domain. See their hostnames and organizations to decide what is legitimate.`,
      },
      high: {
        description: (vars) =>
          `You have ${vars.sourceCount ?? "multiple"} unresolved senders${vars.passRate != null ? ` with a ${Math.round(vars.passRate)}% pass rate` : ""}. Full source details are critical for diagnosis.`,
        urgencyNote: "Limited visibility means slower incident response.",
      },
    },
  },
  remediation_view: {
    title: "Fix Issues, Not Symptoms",
    description:
      "Get step-by-step remediation instructions specific to each failing source and provider.",
    benefits: [
      "Provider-specific fix instructions",
      "Copy-paste DNS records",
      "Prioritized task queue",
    ],
    severityMessages: {
      low: {
        description:
          "A few things need attention before you reach enforcement.",
      },
      medium: {
        description: (vars) =>
          `${vars.taskCount ?? "Several"} sources are failing authentication. Every failure is an opportunity for spoofing.`,
      },
      high: {
        description: (vars) =>
          `${vars.taskCount ?? "Multiple"} sources are failing authentication. Your domain is at serious risk of being spoofed.`,
        urgencyNote:
          "Attackers can send as you and your recipients have no way to tell the difference.",
      },
    },
  },
  // === Starter → Pro gates ===
  remediation_apply: {
    title: "Tired of Copying DNS Records?",
    description:
      "Apply DNS fixes directly to your provider with one click. No manual edits, no typos, no waiting.",
    benefits: [
      "Automatic DNS record updates",
      "Zero manual configuration",
      "Instant validation after changes",
    ],
  },
  apex_links: {
    title: "Stop Resending PDFs",
    description:
      "Share a live dashboard your vendors can bookmark. The data stays current. You see when they view it.",
    benefits: [
      "Shareable live vendor dashboards",
      "Track when vendors view your data",
      "No more re-exporting outdated reports",
    ],
  },
  domain_connect: {
    title: "Automate Your DNS",
    description:
      "Connect your DNS provider for automated record management and one-click fixes.",
    benefits: [
      "One-click SPF/DKIM setup",
      "Automatic record updates",
      "Real-time validation",
    ],
  },
  vendor_pdf: {
    title: "Give Vendors a Report They Will Read",
    description:
      "Generate a professional PDF showing exactly what is failing, why it matters, and what the vendor needs to change.",
    benefits: [
      "Branded PDF exports",
      "Vendor-specific compliance summaries",
      "No more explaining DKIM alignment over email",
    ],
  },
  workspace: {
    title: "Organize Domains into Workspaces",
    description:
      "Separate domains by client, department, or project. Each workspace gets its own dashboard and alerts.",
    benefits: [
      "Logical domain grouping",
      "Team-scoped access",
      "Separate dashboards per workspace",
    ],
  },
  domain_limit: {
    title: "Monitor More Domains",
    description: (vars) =>
      `You are using all ${vars.domainCount ?? 5} domains on your plan. Upgrade to monitor up to 100 domains with flexible pricing.`,
    benefits: [
      "Up to 100 domains (25 base + overage)",
      "Additional domains at $5/domain/month",
      "Full feature access on every domain",
    ],
  },
  // === Pro → Enterprise gates ===
  apex_analytics: {
    title: "Did Your Vendor Actually Look?",
    description:
      "See when vendors open your dashboard, how often they return, and whether they are engaging with remediation steps.",
    benefits: [
      "View and click tracking",
      "Vendor engagement visibility",
      "Follow-up with data, not guesswork",
    ],
  },
  link_expiry: {
    title: "Links That Never Go Stale",
    description:
      "Enterprise links last 90 days. Your vendors can bookmark them and check progress on their own schedule.",
    benefits: [
      "90-day link validity",
      "No more re-sharing broken links",
      "Better vendor experience",
    ],
  },
  sso: {
    title: "Centralize Access with SSO",
    description:
      "Your team authenticates through your existing identity provider. No shared passwords, no manual provisioning.",
    benefits: [
      "SAML and OIDC integration",
      "Automatic user provisioning",
      "Centralized access control",
    ],
  },
  api_access: {
    title: "Build DMARC into Your Workflows",
    description:
      "Pull authentication data into your SIEM, automate remediation, and integrate with your security toolchain.",
    benefits: [
      "Full REST API access",
      "JWT authentication",
      "Custom reporting pipelines",
    ],
  },
};

const TIER_PRICING: Record<string, { price: string; period: string }> = {
  starter: { price: "$29", period: "/month" },
  pro: { price: "$99", period: "/month" },
  enterprise: { price: "$499", period: "/month" },
};

const TIER_DISPLAY_NAMES: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
};

function resolveDescription(
  raw: string | ((vars: DynamicVars) => string),
  vars: DynamicVars
): string {
  return typeof raw === "function" ? raw(vars) : raw;
}

export function UpgradePrompt({
  feature,
  currentTier,
  requiredTier,
  issueCount,
  onUpgrade,
  onDismiss,
  variant = "modal",
  location = "modal",
  severity,
  dynamicVars = {},
}: UpgradePromptProps) {
  const rawMessage = FEATURE_MESSAGES[feature] || {
    title: "Premium Feature",
    description: "This feature requires an upgraded plan.",
    benefits: [],
  };

  // Resolve severity-specific description if available
  const severityMsg =
    severity && rawMessage.severityMessages?.[severity];
  const rawDescription = severityMsg?.description ?? rawMessage.description;
  const description = resolveDescription(rawDescription, dynamicVars);
  const urgencyNote = severityMsg?.urgencyNote;

  const message = {
    title: rawMessage.title,
    description,
    benefits: rawMessage.benefits,
  };

  const pricing = TIER_PRICING[requiredTier];
  const tierName = TIER_DISPLAY_NAMES[requiredTier] || requiredTier;
  const isFreeTier = currentTier === "free";
  const ctaText = isFreeTier ? "Start Free Trial" : "Upgrade Now";
  const ctaShort = isFreeTier
    ? "Start Free Trial"
    : `Upgrade to ${tierName}`;

  // Track prompt impression and clicks
  const promptIdRef = useRef<string | null>(null);
  const hasTrackedRef = useRef(false);

  // Track impression when component mounts
  useEffect(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;

    const trackImpression = async () => {
      try {
        const response = await api.trackUpgradePromptShown({
          feature,
          location,
          targetTier: requiredTier,
        });
        if (response.success) {
          promptIdRef.current = response.promptId;
        }
      } catch (error) {
        // Silently fail - analytics shouldn't block UX
        console.debug("Failed to track upgrade prompt impression:", error);
      }
    };

    trackImpression();
  }, [feature, location, requiredTier]);

  // Handle upgrade click with tracking
  const handleUpgradeClick = useCallback(async () => {
    // Track the click before navigating
    if (promptIdRef.current) {
      try {
        await api.trackUpgradePromptClick(promptIdRef.current);
      } catch (error) {
        // Silently fail
        console.debug("Failed to track upgrade click:", error);
      }
    }
    onUpgrade();
  }, [onUpgrade]);

  if (variant === "banner") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-200 rounded-lg">
              <Lock className="w-4 h-4 text-amber-700" />
            </div>
            <div>
              <p className="font-medium text-amber-900">{message.title}</p>
              <p className="text-sm text-amber-700">{message.description}</p>
            </div>
          </div>
          <button
            onClick={handleUpgradeClick}
            className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors flex items-center gap-2"
          >
            {ctaShort}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-6 h-6 text-amber-600" />
        </div>
        <h3 className="font-semibold text-slate-800 mb-2">{message.title}</h3>
        <p className="text-sm text-slate-600 mb-4">{message.description}</p>
        {issueCount !== undefined && issueCount > 0 && (
          <p className="text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full inline-block mb-4">
            {issueCount} issues detected
          </p>
        )}
        <button
          onClick={handleUpgradeClick}
          className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors"
        >
          {ctaShort} {pricing && `- ${pricing.price}${pricing.period}`}
        </button>
        {isFreeTier && (
          <p className="text-xs text-slate-400 mt-2">
            14-day free trial, cancel anytime
          </p>
        )}
      </div>
    );
  }

  // Modal variant (default)
  return (
    <Modal open onOpenChange={(open) => !open && onDismiss()}>
      <Modal.Content size="md" hideCloseButton className="overflow-hidden">
        {/* Header */}
        <div className="bg-amber-500 p-6 text-slate-900 relative">
          <button
            onClick={onDismiss}
            className="absolute top-4 right-4 p-1 hover:bg-black/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Sparkles className="w-6 h-6" />
            </div>
            <h2 className="text-xl font-bold">{message.title}</h2>
          </div>
          <p className="text-slate-800/80">{message.description}</p>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Urgency note for high severity */}
          {urgencyNote && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{urgencyNote}</p>
            </div>
          )}

          {/* Issue count highlight */}
          {issueCount !== undefined && issueCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
              <p className="font-semibold text-amber-800">
                {issueCount} {issueCount === 1 ? "issue" : "issues"} detected
              </p>
              <p className="text-sm text-amber-700">
                {severity === "high"
                  ? "Immediate attention recommended."
                  : severity === "medium"
                    ? "These should be addressed soon."
                    : "See details and get remediation guidance."}
              </p>
            </div>
          )}

          {/* Benefits list */}
          {message.benefits.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">
                What you'll get
              </p>
              <ul className="space-y-2">
                {message.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-slate-700">
                    <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Pricing */}
          <div className="mb-6">
            <p className="text-slate-600">
              {isFreeTier ? "Try" : "Upgrade to"}{" "}
              <span className="font-semibold text-slate-800">{tierName}</span>
            </p>
            {pricing && (
              <p className="text-3xl font-bold text-slate-800 mt-1">
                {pricing.price}
                <span className="text-base font-normal text-slate-500">
                  {pricing.period}
                </span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onDismiss}
              className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors font-medium"
            >
              Maybe Later
            </button>
            <button
              onClick={handleUpgradeClick}
              className="flex-1 px-4 py-2.5 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors flex items-center justify-center gap-2"
            >
              {ctaText}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {isFreeTier && (
            <p className="text-xs text-slate-400 mt-3 text-center">
              14-day free trial, cancel anytime
            </p>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}

/**
 * LockedFeatureButton - A button that shows a lock icon and triggers upgrade prompt
 */
interface LockedFeatureButtonProps {
  label: string;
  feature: string;
  onClick: () => void;
  className?: string;
}

export function LockedFeatureButton({
  label,
  onClick,
  className = "",
}: LockedFeatureButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors ${className}`}
    >
      <Lock className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

/**
 * UpgradeBadge - Small badge indicating a feature requires upgrade
 */
interface UpgradeBadgeProps {
  tier: string;
  onClick?: () => void;
}

export function UpgradeBadge({ tier, onClick }: UpgradeBadgeProps) {
  const tierName = TIER_DISPLAY_NAMES[tier] || tier;

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium hover:bg-amber-200 transition-colors"
    >
      <Lock className="w-3 h-3" />
      {tierName}
    </button>
  );
}
